import React, { memo, useState, useEffect, useCallback } from "react";

import { validators, formats } from "investira.sdk";
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Icon,
  Typography,
  IconButton,
  Stack,
  Divider,
  Card,
  useMediaQuery,
} from "investira.react.components";
import ConcentracaoGraph from "./ConcentracaoGraph";
import { useConsultaContext } from "../../../../../contexts/ConsultaContext";
import ConcentracaoDetalhesAtivos from "./ConcentracaoDetalhesAtivos";
import ConcentracaoDetalhesEmissor from "./ConcentracaoDetalhesEmissor";

const ConcentracaoDetalhesDialog = memo((props) => {
  const { variant, regra, onSetRegra } = props;
  const { state, actions } = useConsultaContext();

  const isUpMd = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const xDialogProps = isUpMd
    ? {
        fullScreen: true,
        maxWidth: "xl",
      }
    : {
        fullScreen: true,
        PaperProps: {
          sx: {
            margin: 0,
          },
        },
      };

  const isOpen = !validators.isEmpty(regra);

  if (!isOpen) {
    return null;
  }

  const xObservadoColor = regra.grafico.alvo.targetColor;

  return (
    <>
      <Dialog
        fullScreen
        open={isOpen}
        onClose={() => onSetRegra({})}
        {...xDialogProps}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography sx={{ flexGrow: 1 }} variant="h6" component="div">
              {regra.descricao}
            </Typography>
            <IconButton
              edge="end"
              color="primary"
              onClick={() => onSetRegra({})}
              aria-label="close"
            >
              <Icon iconName="cancel" size={21} />
            </IconButton>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ display: "flex", px: 3 }}>
          <Stack id="regraArea" flexGrow={1} sx={{ width: "100%" }}>
            <Stack py={2}>
              <Typography variant="caption">
                {regra.criterio.join(", ")}
              </Typography>
            </Stack>
            <Divider />
            <Stack py={2}>
              <ConcentracaoGraph
                range={regra.grafico.range}
                minimo={regra.grafico.minimo}
                alvo={regra.grafico.alvo}
                observado={regra.grafico.observado}
                enquadrado={regra.grafico.enquadrado}
                targetColor={regra.grafico.alvo.targetColor}
              />
            </Stack>

            <Stack
              sx={{ gap: 2 }}
              component="section"
              direction={{ xs: "column", md: "row" }}
              pb={2}
            >
              {regra.desenquadrado && (
                <Card
                  variant="outlined"
                  sx={{
                    display: "flex",
                    flexGrow: 1,
                  }}
                >
                  <Stack
                    px={2}
                    py={2}
                    flexGrow={1}
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Desenquadrado
                    </Typography>
                    <Stack direction="column" justifyContent="flex-end">
                      <Stack>
                        <Typography variant="h5" color={xObservadoColor}>
                          <b>
                            {regra.desenquadrado &&
                              formats.formatNumber(
                                regra.desenquadrado,
                                2,
                                true
                              )}
                          </b>
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
              )}

              <Stack sx={{ gap: 2, flexGrow: 1 }} direction={"row"}>
                <Card
                  variant="outlined"
                  sx={{
                    display: "flex",
                    flex: 1,
                  }}
                >
                  <Stack
                    px={2}
                    py={2}
                    flexGrow={1}
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Mínimo (%)
                    </Typography>
                    <Stack direction="column" justifyContent="flex-end">
                      <Stack>
                        <Typography variant="h5" color="text.secondary">
                          <b>{regra.minimo}</b>
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
                <Card
                  variant="outlined"
                  sx={{
                    display: "flex",
                    flex: 1,
                  }}
                >
                  <Stack
                    px={2}
                    py={2}
                    flexGrow={1}
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Máximo (%)
                    </Typography>
                    <Stack direction="column" justifyContent="flex-end">
                      <Stack>
                        <Typography variant="h5" color="text.secondary">
                          <b>{regra.maximo}</b>
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
              </Stack>

              <Card
                variant="outlined"
                sx={{
                  display: "flex",
                  flexGrow: 1,
                }}
              >
                <Stack
                  px={2}
                  py={2}
                  flexGrow={1}
                  justifyContent="space-between"
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Observado
                  </Typography>
                  <Stack direction="column" justifyContent="flex-end">
                    <Stack>
                      <Typography variant="h5" color={xObservadoColor}>
                        <b>
                          {regra.financeiro &&
                            formats.formatNumber(regra.financeiro, 2, true)}
                        </b>
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Card>

              <Stack sx={{ gap: 2, flexGrow: 1 }} direction={"row"}>
                <Card
                  variant="outlined"
                  sx={{
                    display: "flex",
                    flex: 1,
                  }}
                >
                  <Stack
                    px={2}
                    py={2}
                    flexGrow={1}
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Observado (%)
                    </Typography>
                    <Stack direction="column" justifyContent="flex-end">
                      <Stack>
                        <Typography variant="h5" color={xObservadoColor}>
                          <b>
                            {regra.observado &&
                              formats.formatNumber(regra.observado, 2, true)}
                          </b>
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
                {regra.alvo && (
                  <Card
                    variant="outlined"
                    sx={{
                      display: "flex",
                      flex: 1,
                    }}
                  >
                    <Stack
                      px={2}
                      py={2}
                      flexGrow={1}
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Alvo (%)
                      </Typography>
                      <Stack direction="column" justifyContent="flex-end">
                        <Stack>
                          <Typography variant="h5" color="info.main">
                            <b>{regra.alvo}</b>
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Card>
                )}
              </Stack>
            </Stack>

            {variant === "SEG" && <ConcentracaoDetalhesAtivos regra={regra} />}

            {variant === "EMI" && <ConcentracaoDetalhesEmissor regra={regra} />}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default ConcentracaoDetalhesDialog;
